import React, { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  TabItem,
  Table,
  Tabs,
  Timeline,
} from "flowbite-react";
import ReactApexChart from "react-apexcharts";
import { ADMIN, API, PAYMENTS, QUERIES } from "../config/apiConfig";
import { useQuery } from "react-query";
import axios from "../config/axiosInstance";
import moment from "moment";
import { Link } from "react-router-dom";
import { formatToReal } from "../utils/utils";
import DrawerLoading from "./DrawerLoading";
import { MdOutlineManageSearch } from "react-icons/md";
import { useApplication } from "../contexts/ApplicationContext";
import { GiReceiveMoney } from "react-icons/gi";
import { IoIosPerson, IoMdBusiness } from "react-icons/io";
import { motion } from "framer-motion";
import {
  FaEye,
  FaEyeSlash,
  FaMoneyBills,
  FaRankingStar,
} from "react-icons/fa6";
import { CopyBlock, dracula } from "react-code-blocks";
import { Tooltip } from "react-tooltip";
import { GoLinkExternal } from "react-icons/go";
import QuickActions from "./QuickActions";
import {
  FaBolt,
  FaBullseye,
  FaCalendarDay,
  FaChartBar,
  FaChartLine,
  FaInfoCircle,
  FaMedal,
  FaMoneyBillWave,
  FaPlus,
  FaPlusCircle,
  FaSearch,
  FaUserCheck,
} from "react-icons/fa";
import { BsSearch } from "react-icons/bs";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination } from "swiper/modules";
import ModalAddCredit from "./modals/ModalAddCredit";
import { HiOutlineCursorClick } from "react-icons/hi";
import { LuTrophy } from "react-icons/lu";

const Dashboard = ({ children }) => {
  const { user, userPermission } = useApplication();
  const [isOpenModalAddCredit, setIsOpenModalAddCredit] = useState(false);
  const [showInvoiceToday, setShowInvoiceToday] = useState(false);
  const [showInvoiceMonth, setShowInvoiceMonth] = useState(false);
  const {
    isLoading,
    error: errorDashboardInfos,
    data: dashboardInfos,
  } = useQuery(["dashboardInfos"], async () => {
    const { data } = await axios.get(`${QUERIES.GET_DASHBOARD_INFOS}`);

    return data.data;
  });

  function getInitials(name) {
    if (!name) return "";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }

  const {
    isLoading: isLoadingRepresentatives,
    error: errorDashboardInfosRepresentatives,
    data: dashboardInfosRepresentative,
  } = useQuery(
    ["dashboardInfosRepresentative"],
    async () => {
      const { data } = await axios.get(
        `${QUERIES.GET_DASHBOARD_INFOS_REPRESENTATIVE}`
      );

      return data.data;
    },
    {
      enabled: user?.Company?.companyTypeId === "REPRESENTANTE",
    }
  );

  const {
    isLoading: isLoadingPowerUser,
    error: errorDashboardInfosPowerUsers,
    data: dashboardInfosPowerUser,
  } = useQuery(
    ["dashboardInfosPowerUser"],
    async () => {
      const { data } = await axios.get(
        `${QUERIES.GET_DASHBOARD_INFOS_POWER_USER}`
      );

      return data.data;
    },
    {
      enabled: userPermission === "POWER_USER",
    }
  );

  const {
    isLoading: isLoadingAccessToday,
    error: errorAccessToday,
    data: accessToday,
  } = useQuery(
    ["accessToday"],
    async () => {
      const { data } = await axios.get(`${ADMIN.GET_ACCESS_TODAY}`);

      return data || 0;
    },
    {
      enabled:
        userPermission === "POWER_USER" ||
        user?.Company?.companyTypeId === "REPRESENTANTE",
    }
  );

  const {
    isLoading: isLoadingInvoicesOpen,
    error: errorInvoicesOpen,
    data: invoicesOpen,
  } = useQuery(["invoicesOpen"], async () => {
    const { data } = await axios.get(`${PAYMENTS.OPEN_PAYMENTS}`);

    return data.data;
  });

  const {
    isLoading: isLoadingToken,
    error: errorToken,
    data: token,
  } = useQuery(
    ["token"],
    async () => {
      const data = await axios.get(`${API.GET_TOKEN}`);

      return data.data.token;

      return null;
    },
    {
      enabled: !!user?.Company?.isIntegrator,
    }
  );

  return (
    <>
      <motion.div
        className="rounded-lg dark:border-gray-700 mt-3"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        {userPermission === "POWER_USER" && (
          <Swiper
            spaceBetween={16}
            slidesPerView={1.2}
            breakpoints={{
              640: {
                slidesPerView: 2.2,
              },
              1250: {
                slidesPerView: 4,
                allowTouchMove: false,
              },
            }}
            modules={[Pagination]}
            pagination={{ clickable: true }}
          >
            {/* 💰 Receita diária */}
            <SwiperSlide>
              <div className="bg-gradient-to-br from-[#162e73] via-[#1f3d9a] to-[#2749c3] text-white p-6 rounded-xl shadow-lg flex justify-between items-center h-full">
                <div>
                  <p className="text-sm opacity-90">Receber hoje</p>
                  <div className="text-2xl font-bold mt-1 flex items-center gap-2">
                    {showInvoiceToday
                      ? formatToReal(
                          dashboardInfosPowerUser?.totalInvoiceToday || 0
                        )
                      : "••••"}
                    <button
                      onClick={() => setShowInvoiceToday(!showInvoiceToday)}
                    >
                      {showInvoiceToday ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  <p className="text-xs opacity-80 mt-1">Faturamento do dia</p>
                </div>
                <FaMoneyBills className="text-white text-3xl opacity-90" />
              </div>
            </SwiperSlide>

            {/* 📆 Receita mensal */}
            <SwiperSlide>
              <div className="bg-gradient-to-br from-[#006D5B] via-[#008978] to-[#00A891] text-white p-6 rounded-xl shadow-lg flex justify-between items-center h-full">
                <div>
                  <p className="text-sm opacity-90">Receber no mês</p>
                  <div className="text-2xl font-bold mt-1 flex items-center gap-2">
                    {showInvoiceMonth
                      ? formatToReal(
                          dashboardInfosPowerUser?.totalInvoiceMonth || 0
                        )
                      : "••••"}
                    <button
                      onClick={() => setShowInvoiceMonth(!showInvoiceMonth)}
                    >
                      {showInvoiceMonth ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  <p className="text-xs opacity-80 mt-1">Faturamento mensal</p>
                </div>
                <GiReceiveMoney className="text-white text-3xl opacity-90" />
              </div>
            </SwiperSlide>

            {/* 👥 Clientes ativos */}
            <SwiperSlide>
              <div className="bg-gradient-to-br from-[#f0f6ff] via-[#e2ecfb] to-[#d4e4f7] border border-blue-200 p-6 rounded-xl shadow-sm hover:shadow-md transition h-full">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-600">Clientes ativos</p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">
                      {dashboardInfosPowerUser?.totalCustomerQuantity ?? 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Contas em operação
                    </p>
                  </div>
                  <IoIosPerson className="text-blue-500 text-3xl" />
                </div>
              </div>
            </SwiperSlide>

            <SwiperSlide>
              <div className="bg-gradient-to-br from-[#f0f6ff] via-[#e2ecfb] to-[#d4e4f7] border border-blue-200 p-6 rounded-xl shadow-sm hover:shadow-md transition h-full relative">
                {isLoadingAccessToday && <DrawerLoading />}
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-600">Acessos hoje</p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">
                      {accessToday?.totalAccesses || 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Usuários únicos no sistema
                    </p>
                  </div>
                  <HiOutlineCursorClick className="text-blue-500 text-3xl" />
                </div>
              </div>
            </SwiperSlide>
          </Swiper>
        )}

        {userPermission === "ADMIN" &&
          user?.Company?.companyTypeId === "REPRESENTANTE" && (
            <Swiper
              spaceBetween={16}
              slidesPerView={1.2}
              breakpoints={{
                640: {
                  slidesPerView: 2.2,
                },
                1250: {
                  slidesPerView: 4,
                  allowTouchMove: false,
                },
              }}
              modules={[Pagination]}
              pagination={{ clickable: true }}
            >
              {/* 💰 Receita diária */}
              <SwiperSlide>
                <div className="bg-gradient-to-br from-[#162e73] via-[#1f3d9a] to-[#2749c3] text-white p-6 rounded-xl shadow-lg flex justify-between items-center h-full">
                  <div>
                    <p className="text-sm opacity-90">Receber hoje</p>
                    <div className="text-2xl font-bold mt-1 flex items-center gap-2">
                      {showInvoiceToday
                        ? formatToReal(
                            dashboardInfosRepresentative?.totalInvoiceToday || 0
                          )
                        : "••••"}
                      <button
                        onClick={() => setShowInvoiceToday(!showInvoiceToday)}
                      >
                        {showInvoiceToday ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    <p className="text-xs opacity-80 mt-1">
                      Faturamento do dia
                    </p>
                  </div>
                  <FaMoneyBills className="text-white text-3xl opacity-90" />
                </div>
              </SwiperSlide>

              {/* 📆 Receita mensal */}
              <SwiperSlide>
                <div className="bg-gradient-to-br from-[#006D5B] via-[#008978] to-[#00A891] text-white p-6 rounded-xl shadow-lg flex justify-between items-center h-full">
                  <div>
                    <p className="text-sm opacity-90">Receber no mês</p>
                    <div className="text-2xl font-bold mt-1 flex items-center gap-2">
                      {showInvoiceMonth
                        ? formatToReal(
                            dashboardInfosRepresentative?.totalInvoiceMonth || 0
                          )
                        : "••••"}
                      <button
                        onClick={() => setShowInvoiceMonth(!showInvoiceMonth)}
                      >
                        {showInvoiceMonth ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    <p className="text-xs opacity-80 mt-1">
                      Faturamento mensal
                    </p>
                  </div>
                  <GiReceiveMoney className="text-white text-3xl opacity-90" />
                </div>
              </SwiperSlide>

              {/* 👥 Clientes ativos */}
              <SwiperSlide>
                <div className="bg-gradient-to-br from-[#f0f6ff] via-[#e2ecfb] to-[#d4e4f7] border border-blue-200 p-6 rounded-xl shadow-sm hover:shadow-md transition h-full">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600">Clientes ativos</p>
                      <p className="text-2xl font-bold text-gray-800 mt-1">
                        {dashboardInfosRepresentative?.totalCustomerQuantity ??
                          0}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Contas em operação
                      </p>
                    </div>
                    <IoIosPerson className="text-blue-500 text-3xl" />
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div className="bg-gradient-to-br from-[#f0f6ff] via-[#e2ecfb] to-[#d4e4f7] border border-blue-200 p-6 rounded-xl shadow-sm hover:shadow-md transition h-full relative">
                  {isLoadingAccessToday && <DrawerLoading />}
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600">Acessos hoje</p>
                      <p className="text-2xl font-bold text-gray-800 mt-1">
                        {accessToday?.totalAccesses || 0}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Usuários únicos no sistema
                      </p>
                    </div>
                    <HiOutlineCursorClick className="text-blue-500 text-3xl" />
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>
          )}

        {invoicesOpen?.length > 0 && (
          <Alert className="mb-4" color="warning" withBorderAccent>
            <div>
              Atenção! Você possui <b>({invoicesOpen?.length})</b> pagamentos em
              aberto!{" "}
              <Link to="/invoices" className="underline">
                Clique aqui para visualizar
              </Link>
            </div>
          </Alert>
        )}
        {(userPermission === "POWER_USER" ||
          user?.Company?.companyTypeId === "REPRESENTANTE" ||
          (user?.companyId == 1 && userPermission === "ADMIN")) && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-4 mb-4">
              {user?.Company?.companyTypeId === "CLIENTE" ? (
                <Card className="card-dashboard relative">
                  <Tabs aria-label="Default tabs" variant="default">
                    <TabItem active title="Informações">
                      <div className="flex flex-col gap-4 text-sm text-gray-700">
                        {/* Plano contratado */}
                        <div className="flex items-center gap-2">
                          <span className="text-lg">📦</span>
                          <span className="text-base">Plano:</span>
                          <span className="rounded-md bg-primary text-white text-xs px-2 py-1 font-semibold">
                            {user?.Company?.typePayment === "POS"
                              ? "Pós-Pago"
                              : "Pré-Pago"}
                          </span>
                        </div>

                        {/* Data de cadastro */}
                        <div className="flex items-center gap-2">
                          <span className="text-lg">📅</span>
                          <span className="text-base">Data de cadastro:</span>
                          <span className="font-semibold">
                            {moment(user?.Company?.createdAt).format(
                              "DD/MM/YYYY"
                            )}
                          </span>
                        </div>

                        {/* Se pré-pago: saldo */}
                        {user?.Company?.typePayment === "PRE" && (
                          <>
                            <div className="flex items-center gap-2">
                              <span className="text-lg">💳</span>
                              <span className="text-base">Saldo:</span>
                              <span className="font-semibold">
                                {formatToReal(user?.Company?.credit)}
                              </span>
                            </div>

                            {user?.Company?.credit <= 10 &&
                              user?.Company?.credit > 0 && (
                                <Alert color="warning" withBorderAccent>
                                  Seu saldo está acabando. Faça uma recarga para
                                  continuar consultando.
                                </Alert>
                              )}

                            {user?.Company?.credit === 0 && (
                              <Alert color="failure" withBorderAccent>
                                Seu saldo acabou! É necessário recarregar para
                                continuar.
                              </Alert>
                            )}
                          </>
                        )}

                        {/* Se pós-pago: VCM e limite */}
                        {user?.Company?.typePayment === "POS" && (
                          <>
                            {user?.Company?.vcm > 0 && (
                              <div className="flex items-center gap-2">
                                <span className="text-lg">📈</span>
                                <span className="text-base">
                                  Consumação Mímima/Mês:
                                </span>
                                <span className="font-semibold">
                                  {formatToReal(user?.Company?.vcm)}
                                </span>
                              </div>
                            )}

                            {user?.Company?.limitUse > 0 && (
                              <div className="flex items-center gap-2">
                                <span className="text-lg">🚫</span>
                                <span className="text-base">
                                  Limite máximo de uso:
                                </span>
                                <span className="font-semibold">
                                  {formatToReal(user?.Company?.limitUse)}
                                </span>
                              </div>
                            )}

                            {+user?.Company?.limitUse !== 0 &&
                              +user?.Company?.limitUse -
                                +dashboardInfos?.totalPriceUsedMonth <=
                                15 &&
                              +user?.Company?.limitUse -
                                +dashboardInfos?.totalPriceUsedMonth >
                                0 && (
                                <Alert color="warning" withBorderAccent>
                                  Você está se aproximando do seu limite mensal
                                  de uso.
                                </Alert>
                              )}

                            {+user?.Company?.limitUse !== 0 &&
                              +user?.Company?.limitUse -
                                +dashboardInfos?.totalPriceUsedMonth <=
                                0 && (
                                <Alert color="failure" withBorderAccent>
                                  Você atingiu seu limite mensal.
                                </Alert>
                              )}
                          </>
                        )}

                        {/* Integração */}
                        {user?.Company?.isIntegrator && (
                          <div className="mt-3">
                            <div className="font-semibold text-sm mb-1">
                              🔗 Token de integração:
                            </div>
                            {token && (
                              <CopyBlock
                                text={token}
                                theme={dracula}
                                codeBlock
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </TabItem>

                    {user?.Company?.typePayment === "POS" && (
                      <TabItem
                        title={`Pagamentos (${dashboardInfos?.totalPendingExpiredInvoices?.length})`}
                      >
                        <div
                          className="overflow-y-auto"
                          style={{ maxHeight: "300px" }}
                        >
                          {dashboardInfos?.totalPendingExpiredInvoices?.length >
                          0 ? (
                            <div className="rounded-md overflow-hidden border border-gray-100 mt-3">
                              <Table striped className="w-full">
                                <Table.Head>
                                  <td className="bg-primary py-1 px-1 text-center font-semibold text-2xs text-white">
                                    Tipo
                                  </td>
                                  <td className="bg-primary py-1 px-1 text-center font-semibold text-2xs text-white">
                                    Dt. Criação
                                  </td>
                                  <td className="bg-primary py-1 px-1 text-center font-semibold text-2xs text-white">
                                    Dt. Vencimento
                                  </td>
                                  <td className="bg-primary py-1 px-1 text-center font-semibold text-2xs text-white">
                                    Valor
                                  </td>
                                  <td className="bg-primary py-1 px-1 text-center font-semibold text-2xs text-white">
                                    Ações
                                  </td>
                                </Table.Head>
                                <Table.Body>
                                  {dashboardInfos?.totalPendingExpiredInvoices?.map(
                                    (info, index) => (
                                      <tr
                                        key={index}
                                        className={`${
                                          index % 2 === 0
                                            ? "bg-white"
                                            : "bg-gray-100"
                                        }`}
                                      >
                                        <td className="p-1 text-center">
                                          {info?.type}
                                        </td>
                                        <td className="p-1 text-center">
                                          {moment(info?.createdAt).format(
                                            "DD/MM/yyyy"
                                          )}
                                        </td>
                                        <td className="p-1 text-center">
                                          {moment(info?.dueDate).format(
                                            "DD/MM/yyyy"
                                          )}
                                        </td>
                                        <td className="p-1 text-center">
                                          {formatToReal(info?.price)}
                                        </td>
                                        <td className="p-1 text-center">
                                          <Tooltip
                                            id="tooltip-link"
                                            content="Visualizar boleto"
                                          />
                                          <a
                                            data-tooltip-id="tooltip-link"
                                            href={info.paymentLink}
                                            target="_blank"
                                            className="inline-block mt-1"
                                          >
                                            <GoLinkExternal fontSize={16} />
                                          </a>
                                        </td>
                                      </tr>
                                    )
                                  )}
                                </Table.Body>
                              </Table>
                            </div>
                          ) : (
                            <div className="flex justify-center items-center h-40 md:h-60 text-sm md:text-base font-semibold">
                              <MdOutlineManageSearch
                                fontSize={25}
                                className="mr-2"
                              />{" "}
                              Nenhum pagamento pendente!
                            </div>
                          )}
                        </div>
                      </TabItem>
                    )}
                  </Tabs>

                  <div className="flex items-center justify-center">
                    {isLoading && <DrawerLoading />}
                  </div>
                </Card>
              ) : (
                <div className="card-dashboard relative rounded-xl bg-white shadow-md p-4">
                  <Tabs aria-label="Default tabs" variant="default">
                    <TabItem active title="Consultas">
                      <div className="flex items-center gap-3 mb-4">
                        <div>
                          <h2 className="text-base font-bold text-gray-800 leading-tight">
                            Clientes com Mais Consultas Hoje
                          </h2>
                          <p className="text-xs text-gray-500 font-medium">
                            Ranking atualizado do dia
                          </p>
                        </div>
                      </div>

                      {dashboardInfos?.mostQueriesDaily?.length > 0 ? (
                        <div className="mt-3 flex flex-col gap-[6px]">
                          {dashboardInfos?.mostQueriesDaily?.map(
                            (info, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-1.5 bg-white hover:border-blue-300 transition-all"
                              >
                                {/* Cliente + Tipo */}
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold shrink-0">
                                    {getInitials(
                                      info.companyTypeCompany === "CPF"
                                        ? info.companyName
                                        : info.companySocialReason
                                    )}
                                  </div>

                                  <div className="min-w-0 flex flex-col leading-tight">
                                    <div>
                                      <a
                                        href={`/admin/customers/${info?.companyId}`}
                                        className="text-[12px] text-gray-800 font-medium truncate max-w-[250px] uppercase cursor-pointer hover:underline block"
                                        title={
                                          info.companyTypeCompany === "CPF"
                                            ? info.companyName
                                            : info.companySocialReason
                                        }
                                      >
                                        {info.companyTypeCompany === "CPF"
                                          ? info.companyName
                                          : info.companySocialReason}
                                      </a>
                                    </div>

                                    <div className="text-[11px] text-gray-500 italic">
                                      {info.companyTypePayment === "POS"
                                        ? "Plano pós-pago"
                                        : "Plano pré-pago"}
                                    </div>
                                  </div>
                                </div>

                                {/* Contagem */}
                                <div className="text-right text-sm font-bold text-gray-900 flex gap-2 items-center">
                                  {index === 0 ? <LuTrophy /> : null}
                                  <div>{info.queryCount}</div>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-40 md:h-60 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 px-4 text-center shadow-sm">
                          <MdOutlineManageSearch
                            className="text-blue-500 mb-2"
                            size={36}
                          />
                          <p className="text-sm md:text-base font-medium">
                            Nenhum cliente realizou consultas hoje!
                          </p>
                        </div>
                      )}
                    </TabItem>
                    {userPermission === "POWER_USER" && (
                      <TabItem title={`Pagamentos`}>
                        <div className="flex items-center gap-3 mb-4">
                          <div>
                            <h2 className="text-base font-bold text-gray-800 leading-tight">
                              Pagamentos
                            </h2>
                            <p className="text-xs text-gray-500 font-medium">
                              Vencidos / Próximos a vencer
                            </p>
                          </div>
                        </div>
                        <div
                          className="overflow-y-auto"
                          style={{ maxHeight: "300px" }}
                        >
                          {dashboardInfosPowerUser?.totalPendingExpiredInvoices
                            ?.length > 0 ? (
                            <div className="rounded-md overflow-hidden border border-gray-100 mt-3">
                              <Table striped className="w-full">
                                <Table.Head>
                                  <td className="bg-primary py-1 px-1 font-semibold text-2xs text-white">
                                    <div>Cliente</div>
                                  </td>
                                  <td className="bg-primary py-1 px-1 text-center font-semibold text-2xs text-white">
                                    Dt. Vencimento
                                  </td>
                                  <td className="bg-primary py-1 px-1 text-center font-semibold text-2xs text-white">
                                    Valor
                                  </td>
                                  <td className="bg-primary py-1 px-1 text-center font-semibold text-2xs text-white">
                                    Ações
                                  </td>
                                </Table.Head>
                                <Table.Body>
                                  {dashboardInfosPowerUser?.totalPendingExpiredInvoices?.map(
                                    (info, index) => (
                                      <tr
                                        key={index}
                                        className={`${
                                          index % 2 === 0
                                            ? "bg-white"
                                            : "bg-gray-100"
                                        }`}
                                      >
                                        <td className="p-1">
                                          <div className="-mb-1">
                                            {info?.Company?.name ||
                                              info?.Company?.socialReason}
                                          </div>
                                          <a
                                            href={`https://wa.me/55${info?.Company?.phoneNumber?.replace(
                                              /\D/g,
                                              ""
                                            )}`}
                                            target="_blank"
                                            className="text-2xs -mt-1 text-green-500 underline"
                                          >
                                            Tel: {info?.Company?.phoneNumber}
                                          </a>
                                        </td>
                                        <td className="p-1 text-center">
                                          {moment(info?.dueDate).format(
                                            "DD/MM/yyyy"
                                          )}
                                        </td>
                                        <td className="p-1 text-center">
                                          {formatToReal(info?.price)}
                                        </td>
                                        <td className="p-1 text-center">
                                          <Tooltip
                                            id="tooltip-link"
                                            content="Visualizar boleto"
                                          />
                                          <a
                                            data-tooltip-id="tooltip-link"
                                            href={info.paymentLink}
                                            target="_blank"
                                            className="inline-block mt-1"
                                          >
                                            <GoLinkExternal fontSize={16} />
                                          </a>
                                        </td>
                                      </tr>
                                    )
                                  )}
                                </Table.Body>
                              </Table>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center h-40 md:h-60 rounded-xl bg-green-50 border border-green-200 text-green-800 px-4 text-center shadow-sm">
                              <MdOutlineManageSearch
                                className="text-green-600 mb-2"
                                size={36}
                              />
                              <p className="text-sm md:text-base font-medium">
                                Nenhum pagamento pendente!
                              </p>
                            </div>
                          )}
                        </div>
                      </TabItem>
                    )}
                  </Tabs>

                  <div className="flex items-center justify-center">
                    {isLoading && <DrawerLoading />}
                  </div>
                </div>
              )}
              <div className="card-dashboard relative rounded-xl bg-white shadow-md p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white p-2 rounded-xl shadow-md">
                    <FaChartLine className="text-white text-lg" />
                  </div>
                  <div>
                    <h2 className="text-base md:text-lg font-bold text-gray-800 leading-tight">
                      Histórico de Consultas
                    </h2>
                    <p className="text-xs text-gray-500 font-medium">
                      Últimos 7 dias
                    </p>
                  </div>
                </div>

                {!isLoading &&
                  dashboardInfos?.lastQueriesBy7Days?.length > 0 && (
                    <ReactApexChart
                      options={{
                        chart: {
                          height: 460,
                          type: "bar",
                          events: {
                            click: function (chart, w, e) {
                              // console.log(chart, w, e)
                            },
                          },
                          toolbar: {
                            show: false,
                          },
                        },
                        plotOptions: {
                          bar: {
                            columnWidth: "45%",
                            distributed: true,
                          },
                        },
                        dataLabels: {
                          enabled: true,
                          style: {
                            fontSize: "13px",
                            fontWeight: "bold",
                            colors: ["#ffffff"],
                          },
                          formatter: function (val) {
                            return val === 0 ? "" : val.toLocaleString("pt-BR");
                          },
                          offsetY: -12,
                        },
                        legend: {
                          show: false,
                        },
                        xaxis: {
                          categories: dashboardInfos?.lastQueriesBy7Days?.map(
                            (obj) => {
                              const [, month, day] = obj.date.split("-");
                              return `${day}/${month}`;
                            }
                          ),
                          labels: {
                            style: {
                              fontSize: "12px",
                            },
                          },
                        },
                      }}
                      series={[
                        {
                          name: "Qtd. de consultas",
                          data: dashboardInfos?.lastQueriesBy7Days?.map(
                            (obj) => obj.totalQueries
                          ),
                        },
                      ]}
                      type="bar"
                      height={460}
                    />
                  )}

                <div className="flex items-center justify-center">
                  {isLoading && <DrawerLoading />}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
              <div className="card-dashboard relative rounded-xl bg-white shadow-md p-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white p-2 rounded-xl shadow-md">
                      <FaSearch className="text-white text-lg" />
                    </div>
                    <div>
                      <h2 className="text-base md:text-lg font-bold text-gray-800 leading-tight">
                        Últimas Consultas
                      </h2>
                      <p className="text-xs text-gray-500 font-medium">
                        Acompanhe as consultas mais recentes
                      </p>
                    </div>
                  </div>
                </div>
                {!isLoading && (
                  <>
                    {dashboardInfos?.lastQueries?.length > 0 ? (
                      <div className="space-y-2">
                        {dashboardInfos.lastQueries.map((query, index) => (
                          <div
                            key={index}
                            className="flex items-start gap-4 bg-white border border-gray-200 shadow-sm rounded-lg p-4 hover:shadow-md transition-shadow"
                          >
                            <div className="mt-1 text-blue-600">
                              <BsSearch size={20} />
                            </div>
                            <div className="flex-1">
                              <div className="text-sm text-gray-500 flex justify-between">
                                <span className="font-medium text-gray-700">
                                  {query.queryName}
                                </span>
                                <span className="text-xs">
                                  {moment(query.createdAt).format(
                                    "DD/MM/YY - HH:mm"
                                  )}
                                  h
                                </span>
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                Usuário:{" "}
                                <span className="font-medium">
                                  {query.userName}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center my-20 font-semibold flex flex-col gap-2 items-center text-gray-600">
                        <MdOutlineManageSearch
                          fontSize={32}
                          className="text-blue-600"
                        />
                        <div>Nenhuma consulta realizada</div>
                      </div>
                    )}
                  </>
                )}
                <div className="flex items-center justify-center">
                  {isLoading && <DrawerLoading />}
                </div>
              </div>
              <div className="card-dashboard relative rounded-xl bg-white shadow-md p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white p-2 rounded-xl shadow-md">
                      <FaRankingStar className="text-white text-lg" />
                    </div>
                    <div>
                      <h2 className="text-base md:text-lg font-bold text-gray-800 leading-tight">
                        Produtos mais consultados
                      </h2>
                      <p className="text-xs text-gray-500 font-medium">
                        No mês
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  {dashboardInfos?.mostSearchedProductsMonth?.length > 0 ? (
                    dashboardInfos.mostSearchedProductsMonth
                      ?.slice(0, 5)
                      ?.map((query, index) => {
                        const getRankingIcon = () => {
                          const baseStyle =
                            "w-10 h-10 rounded-lg flex items-center justify-center shrink-0";
                          switch (index) {
                            case 0:
                              return (
                                <div className={`${baseStyle} bg-yellow-100`}>
                                  <FaMedal
                                    className="text-yellow-500"
                                    title="1º lugar"
                                  />
                                </div>
                              );
                            case 1:
                              return (
                                <div className={`${baseStyle} bg-gray-200`}>
                                  <FaMedal
                                    className="text-gray-500"
                                    title="2º lugar"
                                  />
                                </div>
                              );
                            case 2:
                              return (
                                <div className={`${baseStyle} bg-orange-100`}>
                                  <FaMedal
                                    className="text-orange-500"
                                    title="3º lugar"
                                  />
                                </div>
                              );
                            default:
                              return (
                                <div className={`${baseStyle} bg-blue-100`}>
                                  <span className="text-blue-700 font-bold text-sm">
                                    {index + 1}
                                  </span>
                                </div>
                              );
                          }
                        };

                        return (
                          <div
                            key={index}
                            className="flex items-center gap-2 justify-between rounded-xl border border-gray-200 bg-white shadow-sm px-4 py-3 hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-center gap-3">
                              {getRankingIcon()}
                              <div className="text-sm font-medium text-gray-800 w-full max-w-[200px] truncate">
                                {query.name}
                              </div>
                            </div>
                            <div className="text-sm font-semibold text-gray-700">
                              {query.totalQueries.toLocaleString("pt-BR")}{" "}
                              {query.totalQueries === 1
                                ? "consulta"
                                : "consultas"}
                            </div>
                          </div>
                        );
                      })
                  ) : (
                    <div className="text-center my-20 font-semibold flex flex-col gap-2 items-center text-gray-600">
                      <MdOutlineManageSearch
                        fontSize={32}
                        className="text-blue-600"
                      />
                      <div>Nenhum produto consultado</div>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-center">
                  {isLoading && <DrawerLoading />}
                </div>
              </div>
              <div className="card-dashboard relative rounded-xl bg-white shadow-md p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white p-2 rounded-xl shadow-md">
                    <FaChartBar className="text-white text-lg" />
                  </div>
                  <div>
                    <h2 className="text-base md:text-lg font-bold text-gray-800 leading-tight">
                      Acessos Hoje
                    </h2>
                    <p className="text-xs text-gray-500 font-medium">
                      Quantidade de acessos realizados hoje
                    </p>
                  </div>
                </div>

                <div
                  className="overflow-y-auto pr-2 flex flex-col items-center justify-center"
                  style={{ maxHeight: "380px", minHeight: "200px" }}
                >
                  {accessToday?.clients?.filter((c) => c.accessesToday != 0)
                    ?.length > 0 ? (
                    <>
                      {accessToday?.clients
                        ?.filter((c) => c.accessesToday != 0)
                        ?.map((item) => (
                          <div
                            key={item.companyId}
                            className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3 flex items-center justify-between mb-2 w-full"
                          >
                            <div>
                              <p className="text-sm font-semibold text-gray-800">
                                {item.companyName}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                                {item.accessesToday}
                              </span>
                            </div>
                          </div>
                        ))}
                    </>
                  ) : (
                    <div className="text-center w-full mt-5">
                      Nenhum cliente realizou acesso hoje!
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-center">
                  {isLoading && <DrawerLoading />}
                </div>
              </div>
            </div>
          </>
        )}

        {(user?.Company?.companyTypeId === "CLIENTE" ||
          (user?.companyId == 1 &&
            (userPermission === "SELLER" || userPermission === "USER"))) && (
          <div className="space-y-6">
            {/* Card de boas-vindas com novo layout */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-900 via-blue-700 to-blue-600 shadow-lg border border-white/10 p-6 md:p-10 text-white">
              {/* Brilho decorativo */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />

              <div className="flex flex-col md:flex-row items-center md:items-start gap-6 md:gap-10 relative z-10">
                {/* Ícone de destaque */}
                <div className="flex items-center justify-center w-20 h-20 rounded-2xl bg-white/10 border border-white/20 shadow-inner">
                  <FaRankingStar size={34} className="text-white" />
                </div>

                {/* Texto principal */}
                <div className="flex-1 space-y-3 text-center md:text-left">
                  <h1 className="text-xl md:text-2xl font-bold">
                    Bem-vindo, {user?.name}!
                  </h1>
                  <p className="text-sm md:text-base text-white/90 max-w-3xl leading-relaxed">
                    Veja um panorama completo da sua conta. Acompanhe os dados
                    mais importantes, monitore sua equipe e aproveite os
                    recursos da nossa plataforma.
                  </p>

                  {/* 💰 SALDO DENTRO DO CARD */}
                  {user?.Company?.typePayment === "PRE" && (
                    <div className="w-full max-w-md bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl px-5 py-4 flex items-center justify-between shadow-inner">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/20 rounded-xl">
                          <FaMoneyBillWave size={20} className="text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-white/80">
                            Saldo disponível
                          </p>
                          <p className="text-lg font-bold text-white">
                            {formatToReal(user?.Company?.credit)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setIsOpenModalAddCredit(true)}
                        className="text-sm font-semibold bg-white text-blue-800 px-4 py-1.5 rounded-lg shadow hover:brightness-110 transition"
                      >
                        Recarregar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Ações rápidas */}
            <QuickActions company={user?.Company} />
          </div>
        )}
      </motion.div>
      <ModalAddCredit
        isOpen={isOpenModalAddCredit}
        setOpenModal={setIsOpenModalAddCredit}
      />
    </>
  );
};

export default Dashboard;
